import { useState, useEffect, useRef, useMemo } from "react";
import {
  Button,
  Box,
  CardContent,
  Typography,
  Stack,
  Card,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDevices } from "../../api/deviceAPI";
import { v4 as uuidv4 } from "uuid";
import { postCommand, getCommandResponse } from "../../api/commandAPI";
import { getPlaceIdFromPathname } from "../../utils/pathUtils";

export default function Command() {
  const { t } = useTranslation();
  const location = useLocation();

  // URL'den node_id'yi al (örn: /vending-machines/156 -> 156)
  const node_id = useMemo(() => {
    const rawId = getPlaceIdFromPathname(location.pathname);
    return rawId ? parseInt(rawId, 10) : null;
  }, [location.pathname]);

  const isAdmin = localStorage.getItem("userName") === "admin@letta.com.tr";

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [cmdResponse1, setCmdResponse1] = useState(null);
  const [isLoading1, setIsLoading1] = useState(false);
  const [cmdResponse2, setCmdResponse2] = useState(null);
  const [isLoading2, setIsLoading2] = useState(false);
  const [cmdResponse3, setCmdResponse3] = useState(null);
  const [isLoading3, setIsLoading3] = useState(false);
  const [cmdResponse4, setCmdResponse4] = useState(null);
  const [isLoading4, setIsLoading4] = useState(false);
  const [cmdResponse5, setCmdResponse5] = useState(null);
  const [isLoading5, setIsLoading5] = useState(false);
  const [cmdResponse6, setCmdResponse6] = useState(null);
  const [isLoading6, setIsLoading6] = useState(false);
  const [cmdResponse7, setCmdResponse7] = useState(null);
  const [isLoading7, setIsLoading7] = useState(false);
  const [cmdResponseTest, setCmdResponseTest] = useState(null);
  const [isLoadingTest, setIsLoadingTest] = useState(false);

  const [cmdStr, setCmdStr] = useState("");
  const [cmdVal, setCmdVal] = useState("");
  const [oidReg, setOidReg] = useState("");
  const [oidReg2, setOidReg2] = useState("");
  const [Val, setVal] = useState("");
  const [subject, setSubject] = useState("");
  const [severity, setSeverity] = useState("");

  const pollIntervalRef = useRef(null);
  const [isCommandRunning, setIsCommandRunning] = useState(false);

  // Cihazları API'den çek ve node_id'ye göre filtrele
  useEffect(() => {
    async function fetchDevices() {
      if (!node_id) {
        return;
      }

      setDevicesLoading(true);
      try {
        const response = await getDevices();

        // API doğrudan array veya response.data içinde array döndürebilir
        const devicesData = Array.isArray(response) ? response : response.data;

        if (devicesData && Array.isArray(devicesData)) {
          // node_idNode değeri URL'deki node_id ile eşleşen cihazları filtrele
          const filtered = devicesData.filter(
            (device) => device.node_idNode === node_id
          );
          setDevices(filtered);

          // Eğer sadece bir cihaz varsa otomatik seç
          if (filtered.length === 1) {
            setSelectedDevice(filtered[0].idDevice);
          }
        } else {
          setDevices([]);
        }
      } catch (error) {
        console.error("Error fetching devices:", error);
        setDevices([]);
      } finally {
        setDevicesLoading(false);
      }
    }

    fetchDevices();
  }, [node_id]);

  const handleSendCommand = async () => {
    console.log("handleSendCommand:", { node_id, selectedDevice, cmdStr });
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponse1(t("nodeComponents.command.selectDeviceError"));
      setIsCommandRunning(false);
      return;
    }

    setIsLoading1(true);
    setCmdResponse1(null);
    const cmdKey = uuidv4();

    try {
      await postCommand(node_id, selectedDevice, cmdKey, cmdStr, []);
      console.log("cmdKey:", cmdKey);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );
          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse1(response[0].Value);
            // console.log("response.Value:", response[0].Value);
            setIsLoading1(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse1(t("nodeComponents.command.noResponseError"));
            // console.log("else response", response);

            setIsLoading1(false);
            setIsCommandRunning(false);
          } else {
            console.log("else", response);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse1(t("nodeComponents.command.failedResponseError"));
            setIsLoading1(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading1(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendTest = async () => {
    console.log("handleSendTest:", { node_id, selectedDevice, cmdStr });
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponseTest(t("nodeComponents.command.selectDeviceError"));
      setIsCommandRunning(false);
      return;
    }

    setIsLoadingTest(true);
    setCmdResponseTest(null);
    const cmdKey = uuidv4();

    try {
      await postCommand(node_id, selectedDevice, cmdKey, cmdStr, ["MDB:P,1"]);
      console.log("cmdKey:", cmdKey);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );
          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponseTest(response[0].Value);
            // console.log("response.Value:", response[0].Value);
            setIsLoadingTest(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponseTest(t("nodeComponents.command.noResponseError"));
            // console.log("else response", response);

            setIsLoadingTest(false);
            setIsCommandRunning(false);
          } else {
            console.log("else", response);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponseTest(t("nodeComponents.command.failedResponseError"));
            setIsLoadingTest(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoadingTest(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand2 = async () => {
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponse2("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    setIsLoading2(true);
    setCmdResponse2(null);
    const cmdKey = uuidv4();
    const CmdStr = "oidWrite";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, [
        oidReg.toString(),
        Val.toString(),
      ]);
      // console.log("oidReg:", oidReg);
      // console.log("Val:", Val);
      // console.log("CmdStr:", CmdStr);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse2(response[0].Value);
            setIsLoading2(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse2("No response received after 30 seconds");
            setIsLoading2(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse2("Failed to get response after multiple attempts");
            setIsLoading2(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading2(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand3 = async () => {
    // console.log("handleSendCommand3:", { node_id, selectedDevice, oidReg2 });
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponse3("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    setIsLoading3(true);
    setCmdResponse3(null);
    const cmdKey = uuidv4();
    const CmdStr = "oidRead";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, [
        oidReg2.toString(),
      ]);
      // console.log("oidReg:", oidReg2);
      // console.log("CmdStr:", CmdStr);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse3(response[0].Value);
            setIsLoading3(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse3("No response received after 30 seconds");
            setIsLoading3(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse3("Failed to get response after multiple attempts");
            setIsLoading3(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading3(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand4 = async () => {
    // console.log("handleSendCommand3:", { node_id, selectedDevice, cmdVal });
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponse4("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    setIsLoading4(true);
    setCmdResponse4(null);
    const cmdKey = uuidv4();
    const CmdStr = "cliCmd";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, [
        cmdVal.toString(),
      ]);
      // console.log("cmdVal:", cmdVal);
      // console.log("CmdStr:", CmdStr);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse4(response[0].Value);
            setIsLoading4(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse4("No response received after 30 seconds");
            setIsLoading4(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse4("Failed to get response after multiple attempts");
            setIsLoading4(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading4(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand5 = async () => {
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponse5("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    setIsLoading5(true);
    setCmdResponse5(null);
    const cmdKey = uuidv4();
    const CmdStr = "reset";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, []);
      // console.log("CmdStr:", CmdStr);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse5(response[0].Value);
            setIsLoading5(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse5("No response received after 30 seconds");
            setIsLoading5(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse5("Failed to get response after multiple attempts");
            setIsLoading5(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading5(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand6 = async () => {
    if (isCommandRunning) return;
    setIsCommandRunning(true);
    if (!selectedDevice) {
      setCmdResponse6("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    setIsLoading6(true);
    setCmdResponse6(null);
    const cmdKey = uuidv4();
    const CmdStr = "debug";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, [
        subject.toString(),
        severity.toString(),
      ]);
      // console.log("CmdStr:", CmdStr);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse6(response[0].Value);
            setIsLoading6(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse6("No response received after 30 seconds");
            setIsLoading6(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse6("Failed to get response after multiple attempts");
            setIsLoading6(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading6(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand7 = async () => {
    if (isCommandRunning) return;
    setIsCommandRunning(true);
    if (!selectedDevice) {
      setCmdResponse7("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    setIsLoading7(true);
    setCmdResponse7(null);
    const cmdKey = uuidv4();
    const CmdStr = "getConfig";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, []);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse7(response[0].Value);
            setIsLoading7(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse7("No response received after 30 seconds");
            setIsLoading7(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse7("Failed to get response after multiple attempts");
            setIsLoading7(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading7(false);
      setIsCommandRunning(false);
    }
  };

  return (
    <>
      <Box sx={{ maxWidth: { sm: "100%", md: "1700px" } }}>
        <Box sx={{ mt: 2, mb: 2 }}>
          <FormControl fullWidth sx={{ maxWidth: 300 }}>
            <InputLabel id="device-select-label">
              {t("nodeComponents.command.deviceSerial")}
            </InputLabel>
            <Select
              labelId="device-select-label"
              id="device-select"
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              disabled={devicesLoading || devices.length === 0}
              label={t("nodeComponents.command.deviceSerial")}
            >
              {devices.map((device) => (
                <MenuItem key={device.idDevice} value={device.idDevice}>
                  {device.serialDevice}
                </MenuItem>
              ))}
            </Select>
            {devicesLoading && (
              <Typography variant="caption" sx={{ mt: 1 }}>
                {t("nodeComponents.command.loadingDevices")}
              </Typography>
            )}
            {!devicesLoading && devices.length === 0 && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {t("nodeComponents.command.noDevicesFound")}
              </Typography>
            )}
          </FormControl>
        </Box>

        <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ flexGrow: 1 }}
            >
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="success"
                onClick={async () => {
                  await setCmdStr("cliCmd");
                  setTimeout(() => {
                    handleSendTest();
                  }, 100);
                }}
                startIcon={<SendIcon />}
                sx={{ height: 45, width: 150, color: "white" }}
              >
                {t("nodeComponents.command.makeTestSale", "Test Satışı Yap")}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleSendCommand5}
                startIcon={<SendIcon />}
                sx={{ height: 45, width: 150 }}
              >
                {t("nodeComponents.command.resetDevice")}
              </Button>
            </Stack>
            {isLoading5 && (
              <Typography sx={{ mt: 2 }}>
                {t("nodeComponents.command.loadingResponse")}
              </Typography>
            )}{" "}
            {cmdResponse5 && (
              <Box
                sx={(theme) => ({
                  mt: 2,
                  p: 2,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(66, 66, 66, 0.6)"
                      : "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(81, 81, 81, 1)"
                      : "transparent",
                })}
              >
                <Typography variant="subtitle1">
                  {t("nodeComponents.command.commandResponse")}:
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {cmdResponse5}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
        {isAdmin && (
          <>
            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ flexGrow: 1 }}
                >
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand7}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: 150 }}
                  >
                    {t("nodeComponents.command.getConfig")}
                  </Button>
                </Stack>
                {isLoading7 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse7 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse7}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ flexGrow: 1 }}
                >
                  <TextField
                    label={t("nodeComponents.command.subject")}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 2 }}
                  />
                  <TextField
                    label={t("nodeComponents.command.severity")}
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 2 }}
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand6}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: 150 }}
                  >
                    {t("nodeComponents.command.debugMode")}
                  </Button>
                </Stack>
                {isLoading6 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse6 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse6}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ flexGrow: 1 }}
                >
                  <TextField
                    label={t("nodeComponents.command.oidReg")}
                    value={oidReg2}
                    onChange={(e) => setOidReg2(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 2 }}
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand3}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: 150 }}
                  >
                    {t("nodeComponents.command.readRegister")}
                  </Button>
                </Stack>
                {isLoading3 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse3 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse3}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ flexGrow: 1 }}
                >
                  <TextField
                    label={t("nodeComponents.command.oidReg")}
                    value={oidReg}
                    onChange={(e) => setOidReg(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 2 }}
                  />
                  <TextField
                    label={t("nodeComponents.command.value")}
                    value={Val}
                    onChange={(e) => setVal(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 2 }}
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand2}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: 150 }}
                  >
                    {t("nodeComponents.command.writeRegister")}
                  </Button>
                </Stack>
                {isLoading2 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse2 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse2}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ flexGrow: 1 }}
                >
                  <TextField
                    label={t("nodeComponents.command.cmdVal")}
                    value={cmdVal}
                    onChange={(e) => setCmdVal(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 2, minWidth: 600 }}
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand4}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: 150 }}
                  >
                    {t("nodeComponents.command.sendCliCMD")}
                  </Button>
                </Stack>
                {isLoading4 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse4 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse4}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ flexGrow: 1 }}
                >
                  <TextField
                    label={t("nodeComponents.command.commandString")}
                    value={cmdStr}
                    onChange={(e) => setCmdStr(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 2 }}
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: 150 }}
                  >
                    {t("nodeComponents.command.sendCommand")}
                  </Button>
                </Stack>
                {isLoading1 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse1 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse1}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ flexGrow: 1 }}
            >
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="third"
                onClick={() => {
                  setCmdStr("MDB:P,1");
                  handleSendTest();
                }}
                startIcon={<SendIcon />}
                sx={{ height: 45, width: 200 }}
              >
                Send MDB:P,1
              </Button>
            </Stack>
            {isLoadingTest && (
              <Typography sx={{ mt: 2 }}>
                {t("nodeComponents.command.loadingResponse")}
              </Typography>
            )}{" "}
            {cmdResponseTest && (
              <Box
                sx={(theme) => ({
                  mt: 2,
                  p: 2,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(66, 66, 66, 0.6)"
                      : "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(81, 81, 81, 1)"
                      : "transparent",
                })}
              >
                <Typography variant="subtitle1">
                  {t("nodeComponents.command.commandResponse")}:
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {cmdResponseTest}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card> */}
      </Box>
    </>
  );
}
